/**
 * GridToolbarButton — one icon in the vertical strip anchored to a divider.
 *
 * Icons are inline SVG rather than an icon font or package: the strip needs
 * four of them today, and a dependency for that would cost more than it saves.
 */

import type { ReactNode } from "react";

/**
 * Every glyph is drawn at 19 in a 34 key.
 *
 * It was 15, which left 9px of dead ground on each side and read as a small
 * mark floating in a large button rather than as an icon. The **key** is not
 * what changed — the strip's width and the slot height are load-bearing
 * (`AnnotationTools` matches them, and a 34px strip is what the chevron corner
 * was sized against) — only how much of it the drawing uses.
 *
 * **Strokes scale with the box and are not widened on top of it.** 1.3 units in
 * a 16-unit box drawn at 19 already renders ~1.5 device px, up from ~1.2 at the
 * old 15 — the size alone is what makes the line read. Widening to 1.7 units on
 * top of that reached ~2.2px and was too heavy; these are line drawings, and
 * past a point the strokes close up the shapes they are describing.
 */

export interface GridToolbarButtonProps {
  label: string;
  active?: boolean;
  onClick: () => void;
  children: ReactNode;
}

export function GridToolbarButton({
  label,
  active = false,
  onClick,
  children,
}: GridToolbarButtonProps) {
  return (
    <button
      onClick={onClick}
      title={label}
      aria-label={label}
      aria-pressed={active}
      style={{
        // Matched to `AnnotationTools`' SLOT, because the two halves of the
        // strip are one column of keys and a smaller button above a larger one
        // reads as a mistake rather than as a grouping. 34 is the strip's own
        // width, so a key fills it edge to edge.
        width: 34,
        height: 34,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        border: "1px solid transparent",
        borderRadius: 5,
        // The app background rather than transparent: the strip sits on
        // `--qf-surface-alt`, so a transparent button has no edge at all.
        // Active keeps the selection fill, which is what says "this is on".
        background: active ? "var(--qf-selection)" : "var(--qf-background)",
        // **A token, never `#fff`.** Hardcoded white was invisible on the
        // White theme — a white glyph on a white key — which is the failure
        // D13 exists to prevent: a colour that survives a theme switch stops
        // matching everything around it.
        color: active ? "var(--qf-accent)" : "var(--qf-text)",
        cursor: "pointer",
        padding: 0,
        transition: "background 120ms, color 120ms",
      }}
    >
      {children}
    </button>
  );
}

/**
 * Faders — the conventional "configure this view" glyph, stood upright.
 *
 * It was horizontal sliders, and that is what put three keys of horizontal
 * strokes in a row: the plan's three levels, the data card's ruled lines and
 * these. Rotating this one is the cheapest of the three fixes, because nothing
 * about "sliders" depends on which way they run — and vertical strokes are the
 * one texture the rest of the strip does not already use.
 *
 * Three faders, and the knobs are **staggered on purpose**. At 19px a knob is
 * ~4.5 device px across and the tracks are ~5px apart, so two knobs at the same
 * height touch; set at different heights they never come near each other, and
 * the stagger is also what says these are set to something.
 */
export function EditScreenerIcon() {
  return (
    <svg
      width="19"
      height="19"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <line x1="3.4" y1="2.4" x2="3.4" y2="13.6" />
      <line x1="8" y1="2.4" x2="8" y2="13.6" />
      <line x1="12.6" y1="2.4" x2="12.6" y2="13.6" />
      <circle cx="3.4" cy="9.6" r="1.75" fill="var(--qf-surface-alt)" />
      <circle cx="8" cy="5.6" r="1.75" fill="var(--qf-surface-alt)" />
      <circle cx="12.6" cy="10.2" r="1.75" fill="var(--qf-surface-alt)" />
    </svg>
  );
}

/**
 * A table — the screener itself, brought back.
 *
 * Deliberately not the sliders above: that glyph names "configure this view",
 * and the two share a slot, so a reader who saw only one of them has to be able
 * to tell which state they are in. A header row and a column seam is the least
 * that reads as a grid of rows at 15px.
 */
export function ShowScreenerIcon() {
  return (
    <svg
      width="19"
      height="19"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="1.8" y="2.8" width="12.4" height="10.4" rx="1.4" />
      <line x1="1.8" y1="6.2" x2="14.2" y2="6.2" />
      <line x1="6.6" y1="6.2" x2="6.6" y2="13.2" />
    </svg>
  );
}

/**
 * Two candles — how the price series is drawn.
 *
 * The glyph stays a candlestick whatever the current type is: it names the
 * setting, the way the sliders above name "configure this view", and an icon
 * that morphed into a line or a mountain would read as the chart's state rather
 * than as the control that changes it. Bodies are filled with the surface colour
 * so the wick does not show through them in any theme.
 */
export function ChartTypeIcon() {
  return (
    <svg
      width="19"
      height="19"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <line x1="5" y1="1.6" x2="5" y2="14.4" />
      <rect x="2.9" y="4" width="4.2" height="6" rx="1" fill="var(--qf-surface-alt)" />
      <line x1="11" y1="2.6" x2="11" y2="13.4" />
      <rect x="8.9" y="6" width="4.2" height="5.4" rx="1" fill="var(--qf-surface-alt)" />
    </svg>
  );
}

/**
 * A plotted line with a plus — "add one of these to the chart".
 *
 * A zigzag alone would be a second chart glyph beside the candles above it, and
 * the two would read as two views of the same setting; the plus is what makes
 * this the control that *adds* rather than the one that switches. Drawn in the
 * candle icon's 16×16 box at the same 1.3 stroke, so the strip stays one set.
 */
export function IndicatorIcon() {
  return (
    <svg
      width="19"
      height="19"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="1.6,9.4 4.8,5.2 7.6,7.8 10.8,2.6" />
      <line x1="9.8" y1="12.6" x2="14.4" y2="12.6" />
      <line x1="12.1" y1="10.3" x2="12.1" y2="14.9" />
    </svg>
  );
}

/**
 * A building — the company behind the ticker.
 *
 * The panel's six views are Profile, Peers, Fin St, Summary, News and
 * Sentiment: none of them is a price. So the glyph names the *issuer*, not the
 * series — a lens over a candle stood here briefly and said "look at the chart",
 * which is the one thing this panel does not do, and a plain magnifier would
 * have been the symbol search besides.
 *
 * It was a card of ruled lines before either, which is an honest picture of the
 * panel and the wrong one for this slot: `ShowScreenerIcon` stands *in the same
 * slot* when the screener is hidden, and that is a rounded rect with lines in
 * it. The building is the strip's only stepped outline — no full rect, no
 * circle, and no verticals to confuse with the faders below it.
 *
 * **Windows are dots, and that is the drawing, not a compromise.** A 1.3-unit
 * line with a round cap *is* a circle, and at 19px a square window would be
 * ~2px of stroke with no interior left. Six over four also says "offices"
 * faster than any amount of interior detail would.
 */
export function DataPanelIcon() {
  return (
    <svg
      width="19"
      height="19"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      // The theme's Positive, so the fundamentals key stands out from the
      // strip's grey tools (owner's call, 2026-09-19). A token, so it follows
      // a theme switch; the lit state still shows in the key's background.
      style={{ color: "var(--qf-positive)" }}
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M1.9 13.9V6.8h5.3V2.3h6.9v11.6Z" />
      {/* Tower: three rows of two, centred on 10.65. */}
      <line x1="9.2" y1="5.2" x2="9.2" y2="5.2" />
      <line x1="12.1" y1="5.2" x2="12.1" y2="5.2" />
      <line x1="9.2" y1="8" x2="9.2" y2="8" />
      <line x1="12.1" y1="8" x2="12.1" y2="8" />
      <line x1="9.2" y1="10.8" x2="9.2" y2="10.8" />
      <line x1="12.1" y1="10.8" x2="12.1" y2="10.8" />
      {/* Low wing: two rows of two. */}
      <line x1="3.6" y1="9.4" x2="3.6" y2="9.4" />
      <line x1="5.6" y1="9.4" x2="5.6" y2="9.4" />
      <line x1="3.6" y1="11.8" x2="3.6" y2="11.8" />
      <line x1="5.6" y1="11.8" x2="5.6" y2="11.8" />
    </svg>
  );
}

/**
 * Community — two speech bubbles, a conversation rather than one message.
 *
 * Outlines only, like the rest of the strip; the second bubble is cut where
 * the first overlaps it, so the two read as a pair at 19 px.
 */
export function CommunityIcon() {
  return (
    <svg
      width="19"
      height="19"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M2 3.2h8.2v5.4H5.4L3 10.6V8.6H2Z" />
      <path d="M12.2 5.6H14v5.4h-1v2l-2.4-2H6.8V10" />
    </svg>
  );
}

/**
 * AI — one speech bubble with a spark in it: the chat, but not with people.
 *
 * One bubble where Community has two, so the two keys stacked on the strip
 * read as different things at 19 px.
 */
export function AiIcon() {
  return (
    <svg
      width="19"
      height="19"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M2.2 3h11.6v7.6H7l-3 2.6v-2.6H2.2Z" />
      <path d="M8 4.6l.7 1.6 1.6.7-1.6.7L8 9.2l-.7-1.6-1.6-.7 1.6-.7Z" />
    </svg>
  );
}

/**
 * Split — one box divided in two by a rule.
 *
 * The dock's own shape, drawn the way it will look: a rounded rect for the dock
 * and one line across it. Horizontal whichever way the dock actually splits —
 * the key does not know where the screener is docked, and a glyph that turned
 * with the layout would read as two different keys.
 */
export function SplitIcon() {
  return (
    <svg
      width="19"
      height="19"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="2.2" y="2.2" width="11.6" height="11.6" rx="1.6" />
      <line x1="2.2" y1="8" x2="13.8" y2="8" />
    </svg>
  );
}

/**
 * A ticked checklist — a set of conditions.
 *
 * It was a funnel, the near-universal sign for a *filter*, and that is precisely
 * what the word stopped meaning here. A condition is an expression that is true
 * or false; filtering a list is one thing you do with one, drawing it on a chart
 * as a signal is another, and a backtest is a third. An icon naming the first of
 * three uses has to change again each time a use is added.
 *
 * **No container, and that is what keeps it off `PlanIcon`.** Both carry a tick,
 * which is the one repeat in this strip — but a clipboard is a tall notched
 * outline *holding* a mark, and this is a bare list with no outline at all.
 * Drawn inside a card the two became the same key, which is the collision this
 * strip was untangled from once already.
 *
 * Two rows rather than three: at 19px a third pushes the boxes to ~2.5px and the
 * ticks stop resolving. Two says "a set of them" where one would not.
 */
export function ConditionIcon() {
  return (
    <svg
      width="19"
      height="19"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="1.9" y="2.6" width="4" height="4" rx="0.8" />
      <polyline points="2.9,4.6 3.8,5.5 5.6,3.2" />
      <line x1="7.6" y1="3.4" x2="14.2" y2="3.4" />
      <line x1="7.6" y1="5.8" x2="12.4" y2="5.8" />
      <rect x="1.9" y="9.4" width="4" height="4" rx="0.8" />
      <polyline points="2.9,11.4 3.8,12.3 5.6,10" />
      <line x1="7.6" y1="10.2" x2="14.2" y2="10.2" />
      <line x1="7.6" y1="12.6" x2="12.4" y2="12.6" />
    </svg>
  );
}

/**
 * A clipboard with a tick — the plan, written down before it is taken.
 *
 * It was three horizontal lines, drawn as the target/entry/stop the plan puts on
 * the chart, on the argument that an icon which *is* the thing it draws is the
 * only kind nobody has to learn. That argument is still right on its own and
 * lost anyway: the key sits above the data panel's ruled card and the screener
 * editor's sliders, and all three were horizontal strokes in a 34px box. A
 * reader who has to count strokes to tell three keys apart is not reading an
 * icon. Distinctness between *neighbours* beats fidelity to one of them.
 *
 * The silhouette is what does the work — a notched top edge, which nothing else
 * in the strip has. The clip is drawn last and filled with the surface colour so
 * it cuts the body's top line rather than sitting on it; the tick is the second
 * half, and it is a plan being *set down*, not a plan completed.
 */
export function PlanIcon() {
  return (
    <svg
      width="19"
      height="19"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3.2" y="2.8" width="9.6" height="11.2" rx="1.5" />
      <rect x="5.9" y="1.5" width="4.2" height="2.6" rx="0.9" fill="var(--qf-surface-alt)" />
      <polyline points="5.7,8.4 7.1,9.8 10.5,6.4" />
      <line x1="5.7" y1="11.9" x2="10.5" y2="11.9" />
    </svg>
  );
}
